import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Shield, TrendingUp } from "lucide-react";
import { Navbar, Footer } from "../components/SiteChrome";
import { fetchCatalog, formatUSD } from "../lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/aa264d3f-aaac-46fb-9aa7-21221d0d9e52/images/1c9fb77c38d4b6742e63278a788aedf2549456c68c9d390dab6896bcc020e76c.png";
const BENTO_BG =
  "https://static.prod-images.emergentagent.com/jobs/aa264d3f-aaac-46fb-9aa7-21221d0d9e52/images/32d29451eac6f2dd9285b6ab153a105fc396bd81cb7fb774e09254fe3b6e9f23.png";
const SKYLINE_IMG =
  "https://images.pexels.com/photos/14112274/pexels-photo-14112274.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MEETING_IMG =
  "https://images.pexels.com/photos/8512189/pexels-photo-8512189.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const INDUSTRIES = [
  "Government-adjacent",
  "Healthcare",
  "Agriculture",
  "Insurance",
  "Real Estate",
  "Professional Services",
];

// Bento span pattern (10 services) — Tetris
const SERVICE_SPANS = [
  "md:col-span-7", // 1 audit (wide)
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-6",
  "md:col-span-6",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
];

const Landing = () => {
  const [catalog, setCatalog] = useState({ services: [], bundles: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCatalog()
      .then(setCatalog)
      .catch(() => setCatalog({ services: [], bundles: [] }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-24 md:pb-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-end">
            <div className="md:col-span-7">
              <div className="overline mb-8">Est. 2026 · Sacramento, CA</div>
              <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tight text-[#0F1A36]">
                AI that pays
                <br />
                <span className="italic font-light">for itself</span>
                <span className="text-[#C9A961]">.</span>
              </h1>
              <p className="mt-10 max-w-xl text-lg text-[#5c5a56] leading-relaxed">
                We help Sacramento-area SMBs ship production AI — from a
                low-risk readiness audit to a Fractional AI Officer on retainer.
                Outcomes, not buzzwords.
              </p>
              <div className="mt-12 flex flex-wrap gap-4">
                <a
                  href="#services"
                  data-testid="hero-cta-primary"
                  className="btn-primary"
                >
                  Browse services <ArrowUpRight className="w-4 h-4" />
                </a>
                <a
                  href="#bundles"
                  data-testid="hero-cta-secondary"
                  className="btn-outline"
                >
                  See bundles
                </a>
              </div>
            </div>

            <div className="md:col-span-5 relative">
              <div className="aspect-[4/5] w-full overflow-hidden">
                <img
                  src={HERO_IMG}
                  alt="Agricultural fields meeting digital circuits"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#f5f1e8] border border-[#d4cdb8] p-5 max-w-[240px] hidden md:block">
                <div className="overline mb-2">Trust</div>
                <div className="font-display text-sm font-medium text-[#0F1A36] leading-snug">
                  Senior consultants. Sacramento pricing. Outcome-based scopes.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Industries marquee */}
        <div className="border-y border-[#d4cdb8] py-6 overflow-hidden bg-[#ebe3cf]">
          <div className="animate-marquee flex gap-16 whitespace-nowrap">
            {[...INDUSTRIES, ...INDUSTRIES, ...INDUSTRIES].map((label, i) => (
              <span
                key={`${label}-${i}`}
                className="font-display text-2xl md:text-3xl text-[#0F1A36]/40 tracking-tight"
              >
                {label} <span className="text-[#C9A961]">/</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="overline mb-6">Why us</div>
            <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight leading-tight">
              Built for the way SMBs actually work.
            </h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              {
                icon: TrendingUp,
                title: "Outcome scoped",
                body: "Every engagement ties to a number you care about — hours saved, pipeline created, tickets deflected.",
              },
              {
                icon: Shield,
                title: "Governance-first",
                body: "We ship AI you can defend in front of a board, a regulator, or a skeptical engineer.",
              },
              {
                icon: MapPin,
                title: "Sacramento priced",
                body: "Bay-Area expertise without the Bay-Area invoice. Built for the SMB middle market.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="border-t border-[#0F1A36] pt-6">
                <Icon className="w-5 h-5 text-[#C9A961] mb-6" />
                <div className="font-display text-lg font-semibold mb-2">{title}</div>
                <div className="text-sm text-[#5c5a56] leading-relaxed">{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section
        id="services"
        data-testid="services-section"
        className="relative scroll-mt-20"
        style={{
          backgroundImage: `url(${BENTO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#f5f1e8]/92" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <div className="overline mb-6">Services · 10 offerings</div>
              <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-none max-w-2xl">
                The catalog.
              </h2>
            </div>
            <p className="max-w-md text-[#5c5a56] text-base leading-relaxed">
              Most engagements start with a Readiness Audit. From there we scope
              the smallest thing that creates the biggest impact.
            </p>
          </div>

          {loading ? (
            <div className="text-[#5c5a56]">Loading catalog…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
              {catalog.services.map((svc, idx) => {
                const minPrice = Math.min(...svc.tiers.map((t) => t.price));
                return (
                  <Link
                    key={svc.slug}
                    to={`/services/${svc.slug}`}
                    data-testid={`service-card-${svc.slug}`}
                    className={`card-edge group p-8 md:p-10 col-span-1 ${SERVICE_SPANS[idx] || "md:col-span-4"} flex flex-col justify-between min-h-[280px]`}
                  >
                    <div>
                      <div className="overline mb-5">{svc.category}</div>
                      <div className="font-display text-2xl md:text-3xl font-semibold leading-tight tracking-tight">
                        {svc.name}
                      </div>
                      <div className="mt-3 text-sm text-[#5c5a56] leading-relaxed max-w-md">
                        {svc.tagline}
                      </div>
                    </div>
                    <div className="mt-10 flex items-end justify-between">
                      <div>
                        <div className="text-xs text-[#5c5a56]">from</div>
                        <div className="font-display text-2xl font-semibold">
                          {formatUSD(minPrice)}
                        </div>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-[#0F1A36] group-hover:text-[#C9A961] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* BUNDLES */}
      <section
        id="bundles"
        data-testid="bundles-section"
        className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 scroll-mt-20"
      >
        <div className="mb-16">
          <div className="overline mb-6">Bundles · Productized</div>
          <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-none">
            Three ways
            <br />
            <span className="italic font-light">to commit</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {catalog.bundles.map((bundle, i) => {
            const tier = bundle.tiers[0];
            const isAnchor = i === 2;
            return (
              <div
                key={bundle.slug}
                data-testid={`bundle-card-${bundle.slug}`}
                className={`p-10 transition-shadow duration-300 ${
                  isAnchor
                    ? "md:col-span-6 bg-[#0F1A36] text-[#f5f1e8] border border-[#0F1A36]"
                    : "md:col-span-3 card-edge"
                } flex flex-col`}
              >
                <div
                  className={`overline ${isAnchor ? "!text-[#C9A961]" : ""} mb-5`}
                >
                  {isAnchor ? "Anchor offer" : "Bundle"}
                </div>
                <div className="font-display text-2xl md:text-3xl font-semibold leading-tight mb-3">
                  {bundle.name}
                </div>
                <div
                  className={`text-sm leading-relaxed mb-6 ${
                    isAnchor ? "text-[#f5f1e8]/70" : "text-[#5c5a56]"
                  }`}
                >
                  {bundle.summary}
                </div>
                <ul
                  className={`space-y-2 text-sm mb-8 ${
                    isAnchor ? "text-[#f5f1e8]/80" : "text-[#0F1A36]"
                  }`}
                >
                  {tier.includes.map((inc) => (
                    <li key={inc} className="flex gap-2">
                      <span className="text-[#C9A961]">—</span> {inc}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <div
                    className={`text-xs ${
                      isAnchor ? "text-[#f5f1e8]/60" : "text-[#5c5a56]"
                    }`}
                  >
                    {tier.billing}
                  </div>
                  <div className="font-display text-3xl font-semibold my-2">
                    {formatUSD(tier.price)}
                  </div>
                  <Link
                    to={`/services/${bundle.slug}`}
                    data-testid={`bundle-cta-${bundle.slug}`}
                    className={`mt-4 inline-flex items-center gap-2 font-medium ${
                      isAnchor ? "text-[#C9A961]" : "text-[#C9A961]"
                    }`}
                  >
                    Book this bundle <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="bg-[#ebe3cf]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <img
                src={MEETING_IMG}
                alt="Two consultants reviewing a roadmap"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <div className="md:col-span-7">
              <div className="overline mb-8">From the field</div>
              <blockquote className="font-display font-light text-3xl md:text-5xl leading-tight tracking-tight">
                &ldquo;They scoped the smallest thing that would save us
                <span className="text-[#C9A961]"> 12 hours a week</span> — and
                shipped it in 21 days.&rdquo;
              </blockquote>
              <div className="mt-10 text-sm text-[#5c5a56]">
                COO · Mid-market healthcare services · Sacramento County
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SACRAMENTO STRIP */}
      <section className="relative h-[320px] md:h-[420px] overflow-hidden">
        <img
          src={SKYLINE_IMG}
          alt="Sacramento skyline"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-tint" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 h-full flex flex-col justify-end pb-12">
          <div className="overline">Local · Capitol Mall</div>
          <div className="font-display text-3xl md:text-5xl font-semibold tracking-tight max-w-2xl mt-2">
            Built where you build.
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
